import React, { useEffect, useMemo, useRef, useState } from 'react'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useDispatch } from 'react-redux'
import { convertPxToNumber, isV2Ready } from '../../../utils/helpers.ts'
import IdeyaTypography from '../../../common/antd/ideya-typography.tsx'
import {
  useListFirmOptionsQuery,
  useListUserOptionsQuery,
} from '../../../apis/option/option-api.api.ts'
import {
  selectIdeaListInput,
  setBookId,
  setIdeaListCategory,
  setIdeaListContributor,
  setIdeaListDateRange,
  setIdeaListFirm,
  toggleOnlyMyIdea,
  toggleOnlyPremiumLeague,
} from '../../../redux/slices/idea-list-input.slice.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { selectUser } from '../../../features/user/user.slice.ts'
import { UserInfo } from '../../../apis/auth/auth.def.ts'
import {
  canSeeCategorySelectPermissions,
  canSeeContributorSelectPermissions,
  canSeeFirmSelectPermissions,
  canSeeViewStatistics,
  hasPermission,
} from '../../../utils/permissions.ts'
import { useFixedBreakpoint } from '../../../common/hooks/use-is-mobile.ts'
import Spin from 'antd/es/spin/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Select from 'antd/es/select/index'
import Switch from 'antd/es/switch/index'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getCategoryOptions,
  getMatchedCombinedCategory,
  splitCategory,
} from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import Drawer from 'antd/es/drawer'
import { Dashboard } from '../../summary/dashboard.tsx'
import { BarChartOutlined } from '@ant-design/icons'
import {
  bookApi,
  useLazyGetBookDetailsQuery,
} from '../../../apis/book/book-api.api.ts'
import IdeyaRangePicker from '../../../common/antd/ideya-rangepicker.tsx'
import { USER_CONTROL_COL } from '@/common/constants/user-controls-layout.ts'
import dayjs from 'dayjs'
import { ROLE_CAPTAIN } from '@/router/roles.ts'
import { ShowLiquidatedSwitch } from '../../components/show-liquidated-switch.tsx'

export const IdeaListUserControls: React.FC = () => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const navigate = useNavigate()
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const dispatch = useDispatch()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const location = useLocation()
  const { region, netCategory } = splitCategory(
    userInfo.groupName || ideaListInput.category
  )
  const { t } = useTranslation()
  const [openDashboard, setOpenDashboard] = useState<boolean>(false)
  const [showDashboard, setShowDashboard] = useState<boolean>(false)
  const isInitialUrlLoad = useRef(true)
  const isCaptain = userInfo.groupRole === ROLE_CAPTAIN

  // Only show dashboard after drawer is fully rendered
  const handleAfterOpenChange = (visible: boolean) => {
    setShowDashboard(visible)
  }

  const canFilterByFirm = hasPermission(
    canSeeFirmSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )
  const canFilterByCategory = hasPermission(
    canSeeCategorySelectPermissions({
      groupId: userInfo.groupId,
      groupRole: userInfo.groupRole,
    }),
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const canFilterByContributor = hasPermission(
    canSeeContributorSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const { data: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  const resolvedFirmId = ideaListInput.firm || userInfo.firmId || undefined
  const { data: userOptionData, isLoading: isLoadingUserOptions } =
    useListUserOptionsQuery({
      firmId: resolvedFirmId,
      netCategory: netCategory || undefined,
      region: region || undefined,
    })

  const [
    triggerGetBookDetails,
    {
      data: bookDetailsData,
      isFetching: isFetchingBookDetails,
      isLoading: isLoadingBookDetails,
    },
  ] = useLazyGetBookDetailsQuery()

  // Derived from the option list + selected contributor; no local state needed.
  const contributorName = useMemo(
    () =>
      userOptionData?.data.find((u) => u.value === ideaListInput.contributor)
        ?.label ?? '',
    [userOptionData, ideaListInput.contributor]
  )

  const canViewStatistics =
    hasPermission(
      canSeeViewStatistics,
      userInfo.firmRole,
      userInfo.groupRole,
      userInfo.role
    ) && contributorName

  useEffect(() => {
    if (!isInitialUrlLoad.current) return
    const params = new URLSearchParams(location.search)
    const userId = params.get('userId')
    const startDate = params.get('startDate')
    const endDate = params.get('endDate')
    if (userId) {
      dispatch(setIdeaListContributor(userId))
      dispatch(toggleOnlyMyIdea(false))
      const user = userOptionData?.data.find((u) => u.value === userId)
      if (user) {
        dispatch(setBookId(user.bookId))
        triggerGetBookDetails({ id: user.bookId })
        isInitialUrlLoad.current = false
      }
    } else {
      dispatch(bookApi.util.resetApiState())
    }

    if (startDate && endDate) {
      dispatch(setIdeaListDateRange([startDate, endDate]))
    }
  }, [location.search, dispatch, userOptionData?.data, triggerGetBookDetails])

  useEffect(() => {
    if (bookDetailsData) {
      dispatch(setIdeaListFirm(bookDetailsData.data.firmId))
      const matchedCategory = getMatchedCombinedCategory(
        bookDetailsData.data.region,
        bookDetailsData.data.netCategory,
        t
      )
      if (matchedCategory) {
        dispatch(setIdeaListCategory(matchedCategory))
      } else {
        dispatch(setIdeaListCategory(bookDetailsData.data.netCategory))
      }
    }
  }, [bookDetailsData, dispatch, t])

  // Reconcile the selected contributor against the loaded option list.
  // Runs only when the option list changes (firm/category switch) or the
  // contributor changes externally (URL/persisted state); user-initiated
  // selection is handled synchronously in the Select onChange below.
  // setBookId dispatches with the already-current value are Immer no-ops,
  // so no extra render is triggered on plain reselection.
  const contributor = ideaListInput.contributor
  useEffect(() => {
    if (!userOptionData) return

    const user = userOptionData.data.find((u) => u.value === contributor)

    if (contributor && !user) {
      // Selected contributor no longer exists in the current option list.
      dispatch(setIdeaListContributor(''))
      dispatch(setBookId(''))
      return
    }

    dispatch(setBookId(user?.bookId ?? ''))
  }, [userOptionData, contributor, dispatch])

  if (
    isLoadingFirmOptions ||
    isLoadingUserOptions ||
    isFetchingBookDetails ||
    isLoadingBookDetails
  ) {
    return <Spin />
  }

  return (
    <>
      <Row justify={'space-between'} style={{ gap: themeStyle.marginBase }}>
        <Col xs={24} sm={{ flex: '1' }}>
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginXS),
              convertPxToNumber(themeStyle.marginXS),
            ]}
            align={'middle'}
          >
            {canFilterByCategory && (
              <Col xs={24} sm={USER_CONTROL_COL.select}>
                <Row>
                  <Select
                    showSearch
                    value={ideaListInput.category}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      dispatch(setIdeaListCategory(value))
                      // If category changes, reset contributor back to all
                      dispatch(setIdeaListContributor(''))
                      dispatch(toggleOnlyMyIdea(false))
                    }}
                    options={getCategoryOptions(t)}
                  />
                </Row>
              </Col>
            )}
            {canFilterByFirm && (
              <Col xs={24} sm={USER_CONTROL_COL.select}>
                <Row>
                  <Select
                    showSearch
                    value={ideaListInput.firm}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      dispatch(setIdeaListFirm(value))
                      // If firm changes, reset contributor back to all
                      dispatch(setIdeaListContributor(''))
                      dispatch(toggleOnlyMyIdea(false))
                      navigate('/idea', { replace: true })
                    }}
                    options={[
                      {
                        value: '',
                        label: t('common.user_controls.all_firms'),
                      },
                      ...(firmOptionData?.data.map((firm) => ({
                        value: firm.value,
                        label: firm.label,
                      })) ?? []),
                    ]}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Row>
              </Col>
            )}
            {canFilterByContributor && (
              <Col xs={24} sm={USER_CONTROL_COL.select}>
                <Row>
                  <Select
                    showSearch
                    value={ideaListInput.contributor}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      const selectedContributor = userOptionData?.data.find(
                        (user) => user.value === value
                      )
                      dispatch(setIdeaListContributor(value))
                      dispatch(setBookId(selectedContributor?.bookId ?? ''))
                      dispatch(toggleOnlyMyIdea(false))
                    }}
                    options={[
                      {
                        value: '',
                        label: t('common.user_controls.all_contributors'),
                      },
                      ...(userOptionData?.data.map((user) => ({
                        value: user.value,
                        label: user.label,
                      })) ?? []),
                    ]}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Row>
              </Col>
            )}
            <Col xs={24} sm={USER_CONTROL_COL.dateRange}>
              <Row>
                <IdeyaRangePicker
                  value={[
                    ideaListInput.dateRange[0]
                      ? dayjs(ideaListInput.dateRange[0])
                      : null,
                    ideaListInput.dateRange[1]
                      ? dayjs(ideaListInput.dateRange[1])
                      : null,
                  ]}
                  onChange={(dates) => {
                    if (!dates || !dates[0] || !dates[1]) {
                      dispatch(setIdeaListDateRange([null, null]))
                    } else {
                      dispatch(
                        setIdeaListDateRange([
                          dates[0].format('YYYY-MM-DD'),
                          dates[1].format('YYYY-MM-DD'),
                        ])
                      )
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Row>
            </Col>
            {isCaptain && (
              <Col>
                <Row
                  gutter={[
                    convertPxToNumber(themeStyle.marginXS),
                    convertPxToNumber(themeStyle.marginXS),
                  ]}
                >
                  <Col>
                    <Switch
                      value={ideaListInput.onlyMyIdea}
                      onChange={(value) => {
                        dispatch(toggleOnlyMyIdea(value))
                        // Turning on "Only My Ideas" resets the contributor
                        // filter back to All contributors.
                        if (value) {
                          dispatch(setIdeaListContributor(''))
                          dispatch(setBookId(''))
                        }
                      }}
                    />
                  </Col>
                  <Col>
                    <IdeyaTypography.Text>
                      {t('idea_list.user_controls.only_my_ideas')}
                    </IdeyaTypography.Text>
                  </Col>
                </Row>
              </Col>
            )}
            {isV2Ready && (
              <Col>
                <Row
                  gutter={[
                    convertPxToNumber(themeStyle.marginXS),
                    convertPxToNumber(themeStyle.marginXS),
                  ]}
                >
                  <Col>
                    <Switch
                      value={ideaListInput.onlyPremiumLeague}
                      onChange={(value) =>
                        dispatch(toggleOnlyPremiumLeague(value))
                      }
                    />
                  </Col>
                  <Col>
                    <IdeyaTypography.Text>
                      {t('idea_list.user_controls.only_premium_league')}
                    </IdeyaTypography.Text>
                  </Col>
                </Row>
              </Col>
            )}
            <Col md={USER_CONTROL_COL.switch}>
              <ShowLiquidatedSwitch />
            </Col>
          </Row>
        </Col>
        {canViewStatistics && (
          <Col xs={{ flex: '0' }}>
            <Row
              align={screens.sm ? 'bottom' : 'middle'}
              justify={screens.sm ? 'start' : 'end'}
              style={{ height: '100%', gap: themeStyle.marginBase }}
              wrap={false}
            >
              <IdeyaButton
                icon={<BarChartOutlined />}
                onClick={() => setOpenDashboard(true)}
              >
                {t('idea_list.view_statistics')}
              </IdeyaButton>
            </Row>
          </Col>
        )}
      </Row>
      <Drawer
        open={openDashboard}
        onClose={() => setOpenDashboard(false)}
        width={isMobile ? '100%' : '75%'}
        placement={'right'}
        title={`Dashboard for ${contributorName}`}
        destroyOnHidden
        style={{
          background: `var(--background-image)`,
        }}
        afterOpenChange={handleAfterOpenChange}
      >
        {showDashboard && (
          <Dashboard
            bookId={ideaListInput.bookId}
            contributor={ideaListInput.contributor}
            disableTickerCodeLink
          />
        )}
      </Drawer>
    </>
  )
}
